import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, Send, CheckCircle, AlertCircle } from 'lucide-react';
import './FeedbackForm.css';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
  userAge?: string | number;
  userGrade?: string | number;
  assessmentData?: any;
  childId?: string;
}

interface FeedbackQuestion {
  /** Response key, and the payload field it maps to on submit. */
  id: string;
  field: string;
  question: string;
  options: string[];
}

/** Three questions, asked right after the snapshot is read. */
const FEEDBACK_QUESTIONS: FeedbackQuestion[] = [
  {
    id: 'sounded_like_child',
    field: 'q13_sounded_like_child',
    question: 'Did this sound like your child?',
    options: ['Not really', 'Partly', 'Yes, mostly', 'Yes, exactly'],
  },
  {
    id: 'new_information',
    field: 'q14_new_information',
    question: "Was there anything here you didn't already know?",
    options: ['Nothing new', 'One or two things', 'Quite a lot'],
  },
  {
    id: 'show_to_teacher',
    field: 'q15_show_to_teacher',
    question: "Would you show this to your child's teacher?",
    options: ['Yes', 'Maybe', 'No'],
  },
];

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  isOpen,
  onClose,
  userName = 'Your Child',
  userGrade = '',
  childId
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [feedbackResponses, setFeedbackResponses] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [submitMessageType, setSubmitMessageType] = useState<'success' | 'error' | ''>('');

  const handleClose = () => {
    // Reset local state so the next open starts fresh
    setSubmitMessage('');
    setSubmitMessageType('');
    setCurrentQuestionIndex(0);
    setFeedbackResponses({});
    onClose();
  };

  const currentQuestion = FEEDBACK_QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === FEEDBACK_QUESTIONS.length - 1;
  const firstName = String(userName).split(' ')[0];

  const handleFeedbackResponse = (questionId: string, response: string) => {
    setFeedbackResponses(prev => ({
      ...prev,
      [questionId]: response
    }));
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < FEEDBACK_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitFeedback = async () => {
    try {
      setIsSubmitting(true);
      setSubmitMessage('');
      setSubmitMessageType('');

      // Grade is already known, so it rides along rather than being asked for.
      const payload: Record<string, any> = {
        child_id: childId,
        q1_grade: String(userGrade || ''),
      };
      FEEDBACK_QUESTIONS.forEach((q) => {
        payload[q.field] = feedbackResponses[q.id] || '';
      });

      const { submitFeedbackForm } = await import('../services/api');
      await submitFeedbackForm(payload);

      setSubmitMessage('Thank you for your feedback!');
      setSubmitMessageType('success');
    } catch (err: any) {
      console.error('Submit feedback failed:', err);
      setSubmitMessage(err?.message || 'Failed to submit feedback. Please try again.');
      setSubmitMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const done = submitMessageType === 'success';

  return (
    <div
      className="fb-root fb-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Snapshot feedback"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="fb-sheet">
        {/* ---------- header ---------- */}
        <div className="fb-head">
          <button type="button" className="fb-close" onClick={handleClose} aria-label="Close feedback">
            <X size={17} />
          </button>
          {done ? (
            <>
              <div className="fb-head__badge">✳ All done</div>
              <h2 className="fb-head__title">Thank you for telling us</h2>
            </>
          ) : (
            <>
              <div className="fb-head__badge">✳ Three quick questions</div>
              <h2 className="fb-head__title">How did this land?</h2>
              <p className="fb-head__sub">
                Your answers shape the next snapshot we write for {firstName}. Skip anything you would
                rather not answer.
              </p>
            </>
          )}
        </div>

        {/* ---------- progress ---------- */}
        {!done && (
          <div className="fb-progress">
            <div className="fb-progress__row">
              <span className="fb-progress__section">The snapshot</span>
              <span className="fb-progress__count">
                {currentQuestionIndex + 1} / {FEEDBACK_QUESTIONS.length}
              </span>
            </div>
            <div
              className="fb-track"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={FEEDBACK_QUESTIONS.length}
              aria-valuenow={currentQuestionIndex + 1}
            >
              <div
                className="fb-track__fill"
                style={{ width: `${((currentQuestionIndex + 1) / FEEDBACK_QUESTIONS.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* ---------- body ---------- */}
        <div className="fb-body">
          {done ? (
            <div className="fb-done">
              <div className="fb-done__mark">
                <CheckCircle size={38} strokeWidth={1.8} />
              </div>
              <h3 className="fb-done__title">That helps more than you know</h3>
              <p className="fb-done__body">
                Every answer goes straight into how we read and write these snapshots, so the next one
                lands a little closer to the child you know.
              </p>
            </div>
          ) : (
            <div className="fb-q" key={currentQuestion.id}>
              {submitMessageType === 'error' && (
                <div className="fb-alert" role="alert">
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{submitMessage}</span>
                </div>
              )}

              <h3 className="fb-q__title">{currentQuestion.question}</h3>

              <div className="fb-options" role="radiogroup" aria-label={currentQuestion.question}>
                {currentQuestion.options.map((option) => {
                  const selected = feedbackResponses[currentQuestion.id] === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className="fb-option"
                      data-selected={selected}
                      onClick={() => handleFeedbackResponse(currentQuestion.id, option)}
                    >
                      <span className="fb-option__dot" aria-hidden="true" />
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ---------- footer ---------- */}
        <div className="fb-foot">
          {done ? (
            <>
              <div className="fb-foot__spacer" />
              <button type="button" className="fb-btn fb-btn--primary" onClick={handleClose}>
                Back to the snapshot
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="fb-btn"
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
              >
                <ArrowLeft size={15} />
                Back
              </button>
              <div className="fb-foot__spacer" />
              {!isLastQuestion && (
                <button type="button" className="fb-btn fb-btn--quiet" onClick={goToNextQuestion}>
                  Skip
                </button>
              )}
              {isLastQuestion ? (
                <button
                  type="button"
                  className="fb-btn fb-btn--primary"
                  onClick={submitFeedback}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send size={15} />
                      Send feedback
                    </>
                  )}
                </button>
              ) : (
                <button type="button" className="fb-btn fb-btn--primary" onClick={goToNextQuestion}>
                  Next
                  <ArrowRight size={15} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
